import {
  React, ReactDOM, joinUrl, _n,
} from 'pexui';
import { registerMessageHandler } from '../../services/msgsvc';
import globals from '../../models/globals';
import { flow, getTrackLabel } from '../../models/flow';
import { DocUploadService } from '../../services/docuploadsvc';
import * as utils from '../../utilities/utils';
import { track, setTrackOpts } from '../../utilities/track';
import { Button } from '../comps/uicomps';
import { Html } from '../comps/html';
import { stopBusy } from '../comps/busy';

const LINKEDIN_LOAD_TIMEOUT = 60000; // 1 minute
const BUTTON_DISABLED_SECONDS = 10; // 10 seconds
let windowWatcher = null;
let linkedInWnd = null;

/**
 * The LinkedIn login page allows to be shown only in a standalone window/tab.
 * We use a form to submit to a blank page.
 */
export const LinkedInForm = () => {
  const appsrc = 'getinfowidget';
  const geo = flow.req.market;
  const linkedInUrl = globals.config.linkedInURL || '';
  if (linkedInUrl) {
    const callbackUrl = joinUrl(utils.getSameDomainBaseUrl(), 'linkedin/linkedinresponse.html');
    const errorUrl = joinUrl(utils.getSameDomainBaseUrl(), 'linkedin/linkedinerror.html');

    return (
      <form
        id="infw_linkedin_form"
        name="samlPost"
        method="post"
        action={linkedInUrl}
        target="_blank"
      >
        <input type="hidden" name="appsrc" value={appsrc} />
        <input type="hidden" name="geo" value={geo} />
        <input type="hidden" name="callbackURL" value={callbackUrl} />
        <input type="hidden" name="errorURL" value={errorUrl} />
      </form>
    );
  }
  return <div>{_n('LinkedIn.unavailable.message')}</div>;
};

/**
 * The LinkedIn Pane shown in widget
 */
export const LinkedInPane = ({
  page, node, widget, flow, className,
}) => {
  const linkedInDone = node.files.length > 0 && node.files[0].type === 'info';
  if (linkedInDone) {
    return (
      <div className={className} />
    );
  }
  return (
    <div className={className}>
      <Button
        id={utils.getUniqueId('infw_btn_linkedin', node)}
        className="infw-btn-secondary"
        label={_n('button_linkedin.label')}
        busyZone="widget"
        onClick={(evt) => {
          evt.preventDefault();
          node.messages = [];
          flow.setCurrentNode(node);

          showLinkedIn(page, node);
        }}
      />
      <Html
        className="infw-btn-desc"
        hideEmpty={true}
        content="LinkedIn.button_desc"
      />

      <LinkedInForm />

      <div id="px_l_iframe_holder" />
    </div>
  );
};

export const showLinkedIn = (page, node) => {
  //
  // Message handler for LinkedIn response/error.
  //
  const LinkedInHandler = (e) => {
    const { data } = e;

    if (windowWatcher) {
      clearInterval(windowWatcher);
      windowWatcher = null;
    }

    stopBusy();

    if (utils.beginsWith(data, 'linkedin_success')) {
      // linkedin_success:profileid=Gy0G_EZwAv#!
      const respObj = utils.parseUrlParams(data.substr(17));

      node.files.push({
        type: 'info',
        name: _n('LinkedIn.success.message'),
        completedTime: new Date(),
      });

      track('LinkedIn:Success', { contentName: getTrackLabel(node) });

      DocUploadService.log2Server({
        transaction_id: flow.req.transaction_id,
        log_type: 'INFO',
        originator_id: flow.req.originator_id,
        country_code: flow.req.market,
        process_control_number: flow.req.document_data.process_control_number,
        log_message: `LinkedIn completed successfully: profileId=${respObj.profileid}`,
      });

      flow.raiseInvokerEvent('linkedInResponse', {
        transaction_id: flow.req.transaction_id,
        process_control_number: flow.req.document_data.process_control_number,
        submission_channel_code: 'LINKEDIN',
        documents_requested_code: node.code,
        received_timestamp: utils.getTimestamp(),
        linkedin_response: {
          profileid: respObj.profileid,
        },
      });

      page.forceUpdate();
    } else if (utils.beginsWith(data, 'linkedin_error')) {
      const error = utils.parseUrlParams(data.substr(15));
      const errorKey = `error.${error.errorCode}.message`;
      const errorLogMessage = `errorCode=${error.errorCode} errorDesc=${error.errorDesc}`;

      DocUploadService.log2Server({
        transaction_id: flow.req.transaction_id,
        log_type: 'ERROR',
        originator_id: flow.req.originator_id,
        country_code: flow.req.market,
        process_control_number: flow.req.document_data.process_control_number,
        log_message: `Error from LinkedIn: ${errorLogMessage}`,
      });

      setTrackOpts('LinkedIn', { productsOption: node.code });
      flow.lastError = error.errorCode;

      const errorMessage = _n(errorKey) || error.errorDesc;

      flow.addNodeMessage(node, `!${errorMessage}`, errorKey);
      page.forceUpdate();
    }
  };

  flow.lastChannel = 'LINKEDIN';

  registerMessageHandler([
    'linkedin_success',
    'linkedin_error',
  ], LinkedInHandler);

  node.messages = [];

  const form = utils.getElement('infw_linkedin_form');
  if (form) {
    let isIE = false;
    let timeCounter = 0;

    // IE 11 has a defect not to submit correctly in a new window, use target="_blank" instead
    if (utils.isGoodBrowser()) {
      const left = (screen.width - 546) / 2;
      linkedInWnd = window.open('', 'linkedInWindow', `width=546,height=625,left=${left},top=100,status=yes`);
      form.target = 'linkedInWindow';
    } else {
      // IE doesn't allow to send messages between browser windows/tabs running in different domains.
      // We use a mediator page comm.html inside an iframe to communicate in the middle.
      // widget (invoker domain) <-(inter-frame message)-> comm.html(widget domain) <-(localStorage)-> linkedinresponse.html / linkedinerror.html(widget domain)
      isIE = true;
      const holder = document.getElementById('px_l_iframe_holder');
      if (holder) {
        ReactDOM.render(<iframe
          name="l_frame"
          src={joinUrl(utils.getSameDomainBaseUrl(), 'linkedin/comm.html')}
          width="300"
          height="20"
          frameBorder="0"
        />, holder);
      } else {
        console.error('Fail to find px_l_iframe_holder');
      }
    }

    // check if the window is closed every 1 second
    windowWatcher = setInterval(() => {
      const windowClosed = (!isIE) && linkedInWnd && linkedInWnd.closed;
      const timeOut = isIE && (++timeCounter > BUTTON_DISABLED_SECONDS);
      if (windowClosed || timeOut) {
        clearInterval(windowWatcher);
        windowWatcher = null;
        stopBusy();

        if (windowClosed) {
          const errorKey = 'error.921.message';
          const errorMessage = _n(errorKey) || 'User cancelled, please try again';

          setTrackOpts('LinkedIn', { contentName: getTrackLabel(node) });
          flow.lastError = '921';
          flow.addNodeMessage(node, `!${errorMessage}`, errorKey);
          page.forceUpdate();
        }
      }
    }, 1000);

    track('LinkedIn:Login', { contentName: getTrackLabel(node) });
    // form.target = 'linkedInWindow';
    form.submit();
  } else {
    console.error('Fail to find element infw_linkedin_form.');
    stopBusy();
  }
};



// WEBPACK FOOTER //
// ./application/src/views/linkedin/linkedin.jsx